import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import App from './App.tsx';
// import App from './App.minimal.tsx';
import { AuthProvider } from './contexts/AuthContext';
// import { AuthProvider } from './contexts/AuthContext.debug';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { ThemeProvider } from './components/providers/ThemeProvider';
import { NotificationProvider } from './components/providers/NotificationProvider';
import { WebSocketProvider } from './components/providers/WebSocketProvider';
import { performanceMonitor } from './services/performance';
import { serviceWorkerManager } from './services/serviceWorker';
import './index.css';

// Initialize performance monitoring
console.log('Initializing performance monitoring...');

// Register service worker for production
if (import.meta.env.PROD) {
  serviceWorkerManager.register({
    onUpdate: (registration) => {
      console.log('New version available');
      // Show update notification to user
      if (confirm('New version available! Would you like to update?')) {
        serviceWorkerManager.skipWaiting();
      }
    },
    onSuccess: (registration) => {
      console.log('Service worker registered successfully');
    },
    onError: (error) => {
      console.error('Service worker registration failed:', error);
    },
  });
}

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: (failureCount, error: any) => {
        if (error?.status === 404) return false;
        return failureCount < 3;
      },
    },
  },
});

// Performance monitoring for React Query
queryClient.setMutationDefaults(['api'], {
  onMutate: () => {
    performanceMonitor.markStart('api_mutation');
  },
  onSettled: () => {
    performanceMonitor.markEnd('api_mutation');
  },
});

// Measure initial app load time
performanceMonitor.markStart('app_load');

const AppProviders = ({ children }: { children: React.ReactNode }) => (
  <StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <DndProvider backend={HTML5Backend}>
          <ThemeProvider>
            <AuthProvider>
              <OnboardingProvider>
                <NotificationProvider>
                  <WebSocketProvider>
                    {children}
                  </WebSocketProvider>
                </NotificationProvider>
              </OnboardingProvider>
            </AuthProvider>
          </ThemeProvider>
        </DndProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </StrictMode>
);

// Render app
const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element not found');
}

const root = createRoot(container);

root.render(
  <AppProviders>
    <App />
  </AppProviders>
);

// Mark app load complete
window.addEventListener('load', () => {
  performanceMonitor.markEnd('app_load');
  
  // Log initial performance metrics
  setTimeout(() => {
    const metrics = performanceMonitor.getAllMetrics();
    const webVitals = performanceMonitor.getWebVitals();
    console.log('Performance Metrics:', { metrics, webVitals });
  }, 1000);
});

// Handle visibility change for performance monitoring
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    performanceMonitor.recordMetric('page_visible', Date.now());
  } else {
    performanceMonitor.recordMetric('page_hidden', Date.now());
  }
});

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  performanceMonitor.disconnect();
});